import React, { useState, useEffect, useRef, useMemo, Suspense, createContext } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Physics, useBox, useSphere, useCylinder, useHingeConstraint, useTrimesh, usePlane, Debug } from '@react-three/cannon';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import { startMusic, stopMusic, setMusicPhase, initMusic, cleanupMusic } from './MindstormMusicManager';
import FireworksDisplay from './FireworksDisplay'; // Import Fireworks
import { Environment, Edges, Text, Float, Line, Html } from '@react-three/drei';
import * as THREE from 'three';
import { RotateCcw, Play, Trophy } from 'lucide-react';
import * as Tone from 'tone';
import { getHighScore, trySetHighScore, GAME_KEYS } from '../utils/HighScoreManager';
import ActiveBall from './ActiveBall';
import Flipper from './Flipper';

import PinballInstructionsGraphic from './PinballInstructionsGraphic';

import DebugGrid from './DebugGrid';
import Scoreboard from './Scoreboard';
import DevPanel from './DevPanel';

// Create a Debug Context to expose game state and functions
export const DebugContext = createContext(null);

// --- Constants & Config ---
// Re-oriented for XZ plane (Width=X, Length=Z)
const TABLE_WIDTH = 22;
const TABLE_LENGTH = 40; // Was Height
const BALL_RADIUS = 0.6;
const FLIPPER_LENGTH = 4.2; // Shortened from 4.5
const FLIPPER_WIDTH = 0.8;
const FLIPPER_HEIGHT = 1.0;
const BUMPER_RADIUS = 1.2;

// Colors
const COLOR_BG = '#0f172a';
const COLOR_NEON_CYAN = '#00ffff';
const COLOR_NEON_PINK = '#ff00ff';
const COLOR_NEON_AMBER = '#ffaa00';
const COLOR_BALL = '#ffffff';

// Sound Engine
const useSound = () => {
    const synth = useRef(null);
    useEffect(() => {
        synth.current = new Tone.PolySynth(Tone.Synth).toDestination();
        synth.current.volume.value = -10;
        return () => synth.current?.dispose();
    }, []);

    const lastBumperTime = useRef(0);
    const playBumper = () => {
        const now = Date.now();
        if (now - lastBumperTime.current > 50 && Tone.context.state === 'running') {
            synth.current?.triggerAttackRelease("C5", "32n");
            lastBumperTime.current = now;
        }
    };
    const playFlipper = () => {
        if (Tone.context.state === 'running') synth.current?.triggerAttackRelease("G2", "32n", "+0", 0.5);
    };
    return { playBumper, playFlipper };
};

// --- Visual Components ---

const NeonShape = ({ color, ...props }) => (
    <mesh {...props}>
        <meshStandardMaterial color="#000000" roughness={0.1} metalness={0.8} />
        <Edges color={color} threshold={15} scale={1.0} />
        {props.children}
    </mesh>
);

const BrainDecal = () => (
    <group position={[0, 0.1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <Text
            fontSize={4}
            color={COLOR_NEON_CYAN}
            anchorX="center"
            anchorY="middle"
            characters="🧠"
        >
            🧠
            <meshStandardMaterial emissive={COLOR_NEON_CYAN} emissiveIntensity={2} toneMapped={false} />
        </Text>
    </group>
);



const LaunchButton = ({ onClick, disabled, isRecover }) => (
    // Moved inward (x=-9) and slightly forward (z=16) to match user request
    <group position={[-9, 5, 16]} rotation={[-Math.PI / 2, 0, 0]}>
        <Html transform distanceFactor={10} style={{ transformStyle: 'preserve-3d' }}>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) onClick();
                }}
                onPointerDown={(e) => e.stopPropagation()} // Prevent click-through to canvas
                disabled={disabled}
                className={`
                    flex flex-col items-center justify-center py-3 px-2 rounded-full font-bold text-lg transition-all border-2 select-none
                    ${disabled
                        ? 'bg-slate-900/80 border-slate-700 text-slate-600 cursor-not-allowed opacity-50'
                        : isRecover
                            ? 'bg-red-900/90 border-red-500 text-red-500 hover:bg-red-500/20 active:scale-95 shadow-[0_0_30px_#ef4444] text-shadow-[0_0_10px_#ef4444]'
                            : 'bg-black/90 border-[#39ff14] text-[#39ff14] hover:bg-[#39ff14]/20 active:scale-95 shadow-[0_0_30px_#39ff14,inset_0_0_10px_#39ff14] text-shadow-[0_0_10px_#39ff14]'
                    }
                `}
                style={{ fontFamily: 'monospace', backfaceVisibility: 'hidden' }}
            >
                {isRecover ? (
                    <>
                        <span className="mb-1 text-xs">R</span>
                        <span className="mb-1 text-xs">E</span>
                        <span className="mb-1 text-xs">C</span>
                        <span className="mb-1 text-xs">O</span>
                        <span className="mb-1 text-xs">V</span>
                        <span className="text-xs">E</span>
                        <span className="text-xs">R</span>
                    </>
                ) : (
                    <>
                        <span className="mb-1">L</span>
                        <span className="mb-1">A</span>
                        <span className="mb-1">U</span>
                        <span className="mb-1">N</span>
                        <span className="mb-1">C</span>
                        <span>H</span>
                    </>
                )}
            </button>
        </Html>
        {/* Physical Button Housing */}
        <mesh position={[0, 0, -0.1]}>
            <capsuleGeometry args={[0.8, 3.5, 4, 8]} />
            <meshStandardMaterial color="#1e293b" />
        </mesh>
    </group>
);







const CurvedWall = ({ position, rotation = [0, 0, 0], radius, startAngle, endAngle, height, thickness, color = COLOR_NEON_CYAN, restitution = 0.8, onHit, clockwise = false }) => {
    const { geometry } = useMemo(() => {
        const s = new THREE.Shape();
        s.absarc(0, 0, radius, startAngle, endAngle, clockwise);
        s.absarc(0, 0, radius - thickness, endAngle, startAngle, !clockwise);
        const g = new THREE.ExtrudeGeometry(s, { depth: height, bevelEnabled: false });
        g.translate(0, 0, -height / 2);

        // Fix: Rotate geometry to stand up on XZ plane (Wall orientation)
        g.rotateX(-Math.PI / 2);

        // Fix: Ensure geometry has indices for useTrimesh
        if (!g.index) {
            const count = g.attributes.position.count;
            const indices = Array.from({ length: count }, (_, i) => i);
            g.setIndex(indices);
        }

        return { geometry: g };
    }, [radius, startAngle, endAngle, height, thickness]);

    const [ref] = useTrimesh(() => ({
        args: [geometry.attributes.position.array, geometry.index.array],
        type: 'Static',
        position,
        rotation,
        material: { friction: 0, restitution },
        onCollide: (e) => {
            if (onHit && e.body.name === 'Ball') {
                onHit();
            }
        }
    }));

    return (
        <NeonShape ref={ref} color={color}>
            <primitive object={geometry} />
        </NeonShape>
    );
};



// ... (Bumper, TriangularObstacle, Wall components remain same) ...

const Bumper = ({ position, onHit }) => {
    // Switch to useBox for robust physics (Cylinders can be buggy)
    // Oversized hitbox to prevent tunneling
    const [ref] = useBox(() => ({
        mass: 0,
        type: 'Static',
        args: [BUMPER_RADIUS * 1.5, 5, BUMPER_RADIUS * 1.5],
        position,
        material: { restitution: 1.5 },
        collisionFilterGroup: 1,
        collisionFilterMask: -1, // Collide with EVERYTHING
        onCollide: (e) => {
            if (e.body.name !== 'Bumper') {
                onHit();
                flash();
            }
        }
    }));
    const [emissiveIntensity, setEmissiveIntensity] = useState(0.5);
    const flash = () => {
        setEmissiveIntensity(3);
        setTimeout(() => setEmissiveIntensity(0.5), 100);
    };
    return (
        <mesh ref={ref}>
            <cylinderGeometry args={[BUMPER_RADIUS, BUMPER_RADIUS, 2.5, 16]} />
            <meshStandardMaterial color={COLOR_NEON_PINK} emissive={COLOR_NEON_PINK} emissiveIntensity={emissiveIntensity} toneMapped={false} />
            <Edges color="#ffffff" threshold={15} />
        </mesh>
    );
};







const PolygonWall = ({ points, height = 4, color = COLOR_NEON_PINK, restitution = 1.5, onHit, position = [0, 0, 0], stripes = false }) => {
    const [emissiveIntensity, setEmissiveIntensity] = useState(0); // Default off

    const flash = () => {
        setEmissiveIntensity(3); // Bright flash
        setTimeout(() => setEmissiveIntensity(0), 100);
    };

    // points: Array of [x, z] coordinates relative to position
    const { geometry, stripePositions, centroid } = useMemo(() => {
        const shape = new THREE.Shape();
        const stripePos = [];
        let cx = 0, cy = 0;

        if (points.length > 0) {
            shape.moveTo(points[0][0], points[0][1]);
            cx += points[0][0]; cy += points[0][1];
            for (let i = 1; i < points.length; i++) {
                shape.lineTo(points[i][0], points[i][1]);
                cx += points[i][0]; cy += points[i][1];

                // Stripes logic
                if (stripes) {
                    const p1 = points[i - 1];
                    const p2 = points[i];
                    const dist = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
                    const numStripes = Math.floor(dist / 0.4);
                    for (let j = 0; j <= numStripes; j++) {
                        const t = j / numStripes;
                        if (!isFinite(t)) continue;
                        const x = p1[0] + (p2[0] - p1[0]) * t;
                        const z = p1[1] + (p2[1] - p1[1]) * t;
                        stripePos.push([x, 0, z]);
                    }
                }
            }
            // Closing segment logic (simplified for readability, same as before)
            if (points.length > 2) {
                const first = points[0];
                const last = points[points.length - 1];
                if (first[0] !== last[0] || first[1] !== last[1]) {
                    if (stripes) {
                        const dist = Math.sqrt(Math.pow(first[0] - last[0], 2) + Math.pow(first[1] - last[1], 2));
                        const numStripes = Math.floor(dist / 0.4);
                        for (let j = 0; j < numStripes; j++) {
                            const t = j / numStripes;
                            const x = last[0] + (first[0] - last[0]) * t;
                            const z = last[1] + (first[1] - last[1]) * t;
                            stripePos.push([x, 0, z]);
                        }
                    }
                }
            }
        }

        // Body Geometry
        const g = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false });
        g.translate(0, 0, -height / 2);
        g.rotateX(Math.PI / 2); // Flip flat
        // Ensure indices
        if (!g.index) {
            const count = g.attributes.position.count;
            const indices = Array.from({ length: count }, (_, i) => i);
            g.setIndex(indices);
        }

        cx /= points.length;
        cy /= points.length;

        return { geometry: g, stripePositions: stripePos, centroid: [cx, cy] };
    }, [points, height, stripes]);

    const [ref] = useTrimesh(() => ({
        args: [geometry.attributes.position.array, geometry.index.array],
        type: 'Static',
        position,
        material: { friction: 0, restitution },
        onCollide: (e) => {
            // Debug Collision
            // console.log("PolygonWall Hit:", e.body.name, e.body.userData);

            // Check broadly for 'Ball' in name OR userData.name
            const isBall = e.body.name === 'Ball' || (e.body.userData && e.body.userData.name === 'Ball');

            if (onHit && isBall) {
                onHit();
                flash();
            }
        }
    }));

    return (
        <group position={position}>
            {/* Main Wall Body - Dark 3D Shape with Neon Edges */}
            <mesh ref={ref}>
                <primitive object={geometry} />
                {/* Dark transparent glass look OR solid dark */}
                <meshStandardMaterial
                    color={emissiveIntensity > 0 ? color : "#111"} // Flash fills color? Or use emissive?
                    emissive={color}
                    emissiveIntensity={emissiveIntensity} // Flashes the whole body
                    roughness={0.2}
                    metalness={0.8}
                    transparent={true}
                    opacity={0.9}
                />
                <Edges color={color} threshold={15} scale={1.0} />
            </mesh>

            {/* Edge Stripes (Posts) - Short (Bottom Only) */}
            {stripes && stripePositions.map((pos, idx) => (
                <mesh key={idx} position={[pos[0], -height / 4, pos[2]]}>
                    <cylinderGeometry args={[0.03, 0.03, height / 2, 4]} />
                    <meshBasicMaterial color="#888" />
                </mesh>
            ))}
        </group>
    );
};


const TriangularObstacle = ({ position }) => {
    const [ref] = useCylinder(() => ({
        mass: 0,
        type: 'Static',
        args: [1.5, 1.5, 2, 3], // RadiusTop, RadiusBottom, Height, Segments
        position,
        rotation: [0, Math.PI / 6, 0], // Rotate to point up
        material: { friction: 0, restitution: 1.2 }
    }));

    return (
        <mesh ref={ref}>
            <cylinderGeometry args={[1.5, 1.5, 2, 3]} />
            <meshStandardMaterial color={COLOR_NEON_AMBER} roughness={0.1} metalness={0.8} />
            <Edges color="#ffffff" threshold={15} />
        </mesh>
    );
};

const VisualCurve = ({ position, rotation = [0, 0, 0], radius, startAngle, endAngle, height = 4, thickness = 0.5, color = COLOR_NEON_CYAN }) => {
    const geometry = useMemo(() => {
        const s = new THREE.Shape();
        s.absarc(0, 0, radius, startAngle, endAngle, false);
        s.absarc(0, 0, radius - thickness, endAngle, startAngle, true);
        const g = new THREE.ExtrudeGeometry(s, { depth: height, bevelEnabled: false });
        g.translate(0, 0, -height / 2);
        g.rotateX(-Math.PI / 2);
        return g;
    }, [radius, startAngle, endAngle, height, thickness]);

    return (
        <NeonShape position={position} rotation={rotation} color={color}>
            <primitive object={geometry} />
        </NeonShape>
    );
};

const StarDecal = ({ position }) => (
    <group position={position} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh>
            <circleGeometry args={[0.8, 5]} />
            <meshBasicMaterial color="#fbbf24" transparent opacity={0.8} />
        </mesh>
    </group>
);

const RingDecal = ({ position }) => (
    <mesh position={position} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.8, 32]} />
        <meshBasicMaterial color="#0088ff" transparent opacity={0.4} side={THREE.DoubleSide} />
        <Edges color="#00ffff" threshold={15} scale={1.0} />
    </mesh>
);

const BallChute = ({ position, isOpen }) => {
    const doorRef = useRef();

    useFrame((state, delta) => {
        if (!doorRef.current) return;
        // Animate door: Slide down (Z-axis in local space because of rotation) or Y-axis?
        // Box is rotated [-Math.PI / 2, 0, 0]. Local Y is Global Z.
        // Let's slide it "up" relative to the box to open.

        const targetY = isOpen ? 1.5 : 0; // Open position vs Closed position
        doorRef.current.position.y = THREE.MathUtils.lerp(doorRef.current.position.y, targetY, delta * 5);
    });

    return (
        <group position={position}>
            <NeonShape color={COLOR_NEON_CYAN}>
                <boxGeometry args={[3, 1, 4]} />
            </NeonShape>
            <mesh position={[0, 0.6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <planeGeometry args={[2, 3]} />
                <meshBasicMaterial color="#000000" />
                <Text position={[0, 0, 0.1]} fontSize={0.5} color={COLOR_NEON_CYAN}>
                    EXTRA
                </Text>
            </mesh>
            {/* The Door */}
            <mesh ref={doorRef} position={[0, 0, 2]} rotation={[Math.PI / 2, 0, 0]}>
                <boxGeometry args={[2.8, 0.2, 0.8]} />
                <meshStandardMaterial color="#333" />
            </mesh>
        </group>
    );
};

const Wall = ({ args, position, rotation = [0, 0, 0], color = COLOR_NEON_CYAN }) => {
    const [ref] = useBox(() => ({
        type: 'Static',
        args,
        position,
        rotation,
        material: { friction: 0, restitution: 0.8 } // High restitution to prevent sticking
    }));
    return (
        <NeonShape ref={ref} color={color}>
            <boxGeometry args={args} />
        </NeonShape>
    );
};

const RolloverTrigger = ({ position, letter, isLit, onHit }) => {
    const onHitRef = useRef(onHit);
    useEffect(() => { onHitRef.current = onHit; }, [onHit]);

    const [ref] = useCylinder(() => ({
        isTrigger: true, // Sensor only, no physical collision
        args: [1.5, 1.5, 0.5, 16], // RadiusTop, RadiusBottom, Height, Segments
        position,
        onCollide: (e) => {
            if (e.body.name === 'Ball') {
                onHitRef.current();
            }
        }
    }));

    // Ring Colors (Pink)
    const ringColor = isLit ? '#ff00ff' : '#4a044a';
    const ringEmissive = isLit ? 3 : 0.5;

    // Text Colors (Cyan)
    const textColor = isLit ? '#00ffff' : '#004488';
    const textEmissive = isLit ? 2 : 0.5;

    return (
        <group ref={ref}>
            {/* Visual Ring - Neon Tube */}
            {/* Raised to -0.05 relative to group (y=0.1) -> World y=0.05 (Above floor y=0) */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]}>
                <torusGeometry args={[1.1, 0.05, 8, 32]} />
                <meshStandardMaterial color={ringColor} emissive={ringColor} emissiveIntensity={ringEmissive} toneMapped={false} />
            </mesh>
            {/* Letter Text */}
            <Text
                position={[0, 0.1, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                fontSize={1.5}
                color={textColor}
                anchorX="center"
                anchorY="middle"
            >
                {letter}
                <meshStandardMaterial emissive={textColor} emissiveIntensity={textEmissive} toneMapped={false} />
            </Text>
        </group>
    );
};

const MemoryBank = ({ position, onBallEnter }) => {
    // Use ref to access latest callback without re-creating physics body
    const onBallEnterRef = useRef(onBallEnter);
    useEffect(() => { onBallEnterRef.current = onBallEnter; }, [onBallEnter]);

    const [ref] = useCylinder(() => ({
        isTrigger: true,
        args: [2, 2, 0.5, 16],
        position,
        onCollide: (e) => {
            if (e.body.userData && e.body.userData.name === 'Ball') {
                onBallEnterRef.current(e.body.userData.id);
            }
        }
    }));

    return (
        <group ref={ref}>
            {/* Visual Saucer */}
            <mesh rotation={[0, 0, 0]}>
                <cylinderGeometry args={[2, 2, 0.2, 32]} />
                <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={2} toneMapped={false} />
            </mesh>
            {/* Inner Depression */}
            <mesh position={[0, 0.1, 0]}>
                <cylinderGeometry args={[1.5, 0.5, 0.5, 32]} />
                <meshStandardMaterial color="#333" />
            </mesh>
        </group>
    );
};

function GlassCeiling() {
    useBox(() => ({
        args: [30, 2, 50], // Thicker box (2 units) to prevent tunneling
        position: [0, 5, 0], // Centered higher to accommodate thickness
        type: 'Static'
    }));
    return null;
}

const KickbackSensor = ({ position, rotation = [0, 0, 0], side, active, onKick }) => {
    const onKickRef = useRef(onKick);
    const activeRef = useRef(active);

    useEffect(() => { onKickRef.current = onKick; }, [onKick]);
    useEffect(() => { activeRef.current = active; }, [active]);

    const [ref] = useBox(() => ({
        isTrigger: true,
        args: [1, 1, 3], // Thin strip
        position,
        rotation,
        onCollide: (e) => {
            if (activeRef.current && e.body.userData && e.body.userData.name === 'Ball') {
                onKickRef.current(e.body.userData.id);
                triggerFlash();
            }
        }
    }));

    // Visual Piston Animation
    const pistonRef = useRef();
    const [flashIntensity, setFlashIntensity] = useState(0);

    const triggerFlash = () => {
        setFlashIntensity(5); // Bright flash
        // Piston punch - Move along local X axis (which is rotated)
        if (pistonRef.current) {
            // Punch OUT (towards center)
            // Left Side (Rotated -45): Local +X points Right-Up
            // Right Side (Rotated +45): Local -X points Left-Up? No, let's check geometry.
            // Box is [0.5, 1, 2].
            // If Side LEFT: Piston at [-1, 0, 0]. Punch to [1.5, 0, 0]?
            // Let's just animate local X.
            const punchDist = side === 'LEFT' ? 1.5 : -1.5;
            pistonRef.current.position.x = punchDist;
            setTimeout(() => {
                if (pistonRef.current) pistonRef.current.position.x = side === 'LEFT' ? -0.5 : 0.5; // Retract
            }, 100);
        }
    };

    useFrame((state, delta) => {
        if (flashIntensity > 0) {
            setFlashIntensity(prev => Math.max(0, prev - delta * 10));
        }

        if (!pistonRef.current) return;

        // Idle state (if not punching)
        if (flashIntensity <= 0) {
            if (active) {
                pistonRef.current.position.x = 0;
            } else {
                // Retracted / Spent
                pistonRef.current.position.x = side === 'LEFT' ? -0.5 : 0.5;
            }
        }
    });

    return (
        <group ref={ref}>
            {/* Piston Visual */}
            <group position={[side === 'LEFT' ? -1 : 1, 0, 0]}>
                <mesh ref={pistonRef}>
                    <boxGeometry args={[0.5, 1, 2]} />
                    <meshStandardMaterial
                        color={active ? "#ffffff" : "#333333"}
                        emissive={active ? "#ffffff" : "#ff0000"}
                        emissiveIntensity={active ? 1 + flashIntensity : 0}
                        toneMapped={false}
                    />
                </mesh>
            </group>
        </group>
    );
};

const Table = ({ onScore, onReset, playSound, ballRefs, onZoneChange, focusLettersLit, onRolloverHit, balls, onBallEnterBank, kickbacks, onKickback, isChuteOpen, onStuck }) => {
    return (
        <group>
            {/* Level Design Grid */}
            <DebugGrid visible={false} />

            {/* --- THE CLIFF ARCHITECTURE --- */}

            {/* ... (Existing Floor & Walls) ... */}
            {/* 1. Main Playfield Floor (Lower Level) */}
            {/* Visual Floor */}
            <mesh receiveShadow position={[0, -1, 0]}>
                <boxGeometry args={[TABLE_WIDTH + 2, 2, TABLE_LENGTH + 2]} />
                <meshStandardMaterial color="#020617" roughness={0.1} metalness={0.8} />
            </mesh>
            {/* Physics Floor (Invisible) */}
            <Wall args={[TABLE_WIDTH + 2, 2, TABLE_LENGTH + 2]} position={[0, -1, 0]} color="black" />

            {/* ... (Rest of Walls & Plunger) ... */}
            {/* 2. Plunger Lane Floor (Upper Level - "The Cliff") */}
            <Wall
                args={[3, 1, 35]}
                position={[8.5, 0.5, 5]}
                color={COLOR_NEON_CYAN}
            />
            {/* Plunger Ramp/Launch Pad */}
            <Wall args={[3, 4, 1]} position={[8.5, 2, 20]} color={COLOR_NEON_CYAN} />

            {/* Brain Decal */}
            <BrainDecal />

            {/* Side Walls */}
            <Wall args={[1, 4, TABLE_LENGTH]} position={[-11.5, 2, 0]} />
            <Wall args={[1, 4, TABLE_LENGTH]} position={[12.5, 2, 0]} />
            <Wall args={[4, 8, TABLE_LENGTH]} position={[-14, 2, 0]} color="black" />
            <Wall args={[4, 8, TABLE_LENGTH]} position={[15, 2, 0]} color="black" />

            {/* Top Wall */}
            <Wall args={[30, 8, 4]} position={[0, 2, -22]} />

            {/* Plunger Lane */}
            <Wall args={[1, 4, 25]} position={[8, 1, 7.5]} />
            <Wall args={[4, 4, 1]} position={[10, 2, 20]} />

            {/* Plunger Deflector */}
            <CurvedWall
                position={[0, 1, -10]}
                rotation={[0, 0, 0]}
                radius={12.5} // Increased to align with outer wall (12.5)
                startAngle={0} // Start exactly at 3 o'clock to avoid lip
                endAngle={Math.PI / 2}
                height={4}
                thickness={0.5}
            />

            {/* Inner Lane (Left) - Restored */}
            <CurvedWall
                position={[-6, 1, -1.5]}
                rotation={[0, Math.PI, 0]}
                radius={6}
                startAngle={0}
                endAngle={Math.PI / 2}
                height={4}
                thickness={0.5}
                color={COLOR_NEON_CYAN}
            />

            {/* Slingshots - Refined Triangular Wedges (Scaled +20%) */}
            {/* Left Wedge: Shifted further left to x=-4.5. Width 2.4, Height 4.2 */}
            <PolygonWall
                points={[
                    [-4.5, 9.8],  // Top (Back corner)
                    [-2.1, 14.0], // Bottom Inner (Tip near flipper)
                    [-4.5, 14.0]  // Bottom Outer (Back corner)
                ]}
                height={4}
                color={COLOR_NEON_PINK}
                stripes={true}
                onHit={() => { onScore(200, "LEFT SLING!"); playSound.playBumper([-3.5, 1, 12]); }}
            />

            {/* Right Wedge: Anchor at Outer Bottom (3.5, 14). Width 2.4, Height 4.2 */}
            <PolygonWall
                points={[
                    [3.5, 9.8],   // Top (Back corner)
                    [1.1, 14.0],  // Bottom Inner (Tip near flipper)
                    [3.5, 14.0]   // Bottom Outer (Back corner)
                ]}
                height={4}
                color={COLOR_NEON_PINK}
                stripes={true}
                onHit={() => { onScore(200, "RIGHT SLING!"); playSound.playBumper([2.5, 1, 12]); }}
            />

            {/* Teal Lanes (Center Curve) */}
            <CurvedWall
                position={[4, 1, 0]}
                radius={8}
                startAngle={0}
                endAngle={Math.PI * 0.3}
                color={COLOR_NEON_CYAN}
                rotation={[0, Math.PI, 0]}
                height={4}
                thickness={0.5}
            />

            {/* --- ROLLOVER TRIGGERS (F-O-C-U-S) --- */}
            <RolloverTrigger position={[-4.125, 0.1, -11.25]} letter="F" isLit={focusLettersLit[0]} onHit={() => onRolloverHit(0)} />
            <RolloverTrigger position={[-1.375, 0.1, -6.25]} letter="O" isLit={focusLettersLit[1]} onHit={() => onRolloverHit(1)} />
            <RolloverTrigger position={[4.125, 0.1, -1.25]} letter="C" isLit={focusLettersLit[2]} onHit={() => onRolloverHit(2)} />
            <RolloverTrigger position={[4.125, 0.1, 3.75]} letter="U" isLit={focusLettersLit[3]} onHit={() => onRolloverHit(3)} />
            <RolloverTrigger position={[1.375, 0.1, 8.75]} letter="S" isLit={focusLettersLit[4]} onHit={() => onRolloverHit(4)} />

            {/* --- MEMORY BANK (Zone 58) --- */}
            <MemoryBank position={[-6.875, 0.1, -1.25]} onBallEnter={onBallEnterBank} />

            {/* --- KICKBACK SENSORS --- */}
            {/* Left Outlane (Zone 114) - Rotated 45 deg (Right-Up) */}
            <KickbackSensor
                position={[-6.5, 0.5, 16.0]}
                rotation={[0, Math.PI / 4, 0]} // Rotate +45 deg (Point Right-Up)
                side="LEFT"
                active={kickbacks.left}
                onKick={(id) => onKickback('left', id)}
            />
            {/* Right Outlane (Between 118-119) - Rotated 45 deg (Left-Up) */}
            <KickbackSensor
                position={[5.5, 0.5, 16.0]}
                rotation={[0, -Math.PI / 4, 0]} // Rotate -45 deg (Point Left-Up)
                side="RIGHT"
                active={kickbacks.right}
                onKick={(id) => onKickback('right', id)}
            />

            {/* Bumpers */}
            <Bumper position={[0, 1.5, -12]} onHit={() => { onScore(100, "JACKPOT!"); playSound.playBumper([0, 1.5, -12]); }} />
            <StarDecal position={[0, 2.8, -12]} />
            <Bumper position={[-7, 1.5, -9]} onHit={() => { onScore(100, "BUMPER HIT!"); playSound.playBumper([-7, 1.5, -9]); }} />
            <StarDecal position={[-7, 2.8, -9]} />
            <Bumper position={[5.5, 1.5, -9]} onHit={() => { onScore(100, "BUMPER HIT!"); playSound.playBumper([5.5, 1.5, -9]); }} />
            <StarDecal position={[5.5, 2.8, -9]} />
            <Bumper position={[-4, 1.5, -5]} onHit={() => { onScore(100, "BUMPER HIT!"); playSound.playBumper([-4, 1.5, -5]); }} />
            <StarDecal position={[-4, 2.8, -5]} />
            <Bumper position={[4, 1.5, -5]} onHit={() => { onScore(100, "BUMPER HIT!"); playSound.playBumper([4, 1.5, -5]); }} />
            <StarDecal position={[4, 2.8, -5]} />

            {/* Ball Chute */}
            <group position={[2, 4, -18]} rotation={[Math.PI / 2, 0, 0]}>
                <BallChute position={[0, 0, 0]} isOpen={isChuteOpen} />
            </group>

            {/* Flippers */}
            <Flipper type="LEFT" position={[-5, 0.5, 16]} onFlip={playSound.playFlipper} />
            <Flipper type="RIGHT" position={[3, 0.5, 16]} onFlip={playSound.playFlipper} />

            {/* Active Balls */}
            {balls.map(ball => (
                <ActiveBall
                    key={ball.id}
                    ref={(el) => {
                        if (el) ballRefs.current.set(ball.id, el);
                        else ballRefs.current.delete(ball.id);
                    }}
                    id={ball.id}
                    position={ball.position}
                    isLocked={ball.isLocked}
                    lockPosition={ball.lockPosition}
                    onReset={onReset}
                    onZoneChange={onZoneChange}
                    onDrain={() => onReset(ball.id)} // onReset now handles drain by ID
                    onStuck={onStuck}
                />
            ))}
        </group>
    );
};

const MindstormPinball = () => {
    // --- State Hooks ---
    const [gameState, setGameState] = useState('START');
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(0);
    const [isNewHighScore, setIsNewHighScore] = useState(false);
    const [lives, setLives] = useState(3);
    const [sfxEnabled, setSfxEnabled] = useState(true);
    const [musicEnabled, setMusicEnabled] = useState(true);
    const [focusLettersLit, setFocusLettersLit] = useState([false, false, false, false, false]);

    // Multiball State
    const [balls, setBalls] = useState([]); // { id, position, isLocked, lockPosition }
    const nextBallId = useRef(0);
    const [lockCount, setLockCount] = useState(0);
    const lockCountRef = useRef(0); // Ref for synchronous logic
    const [isBallInPlunger, setIsBallInPlunger] = useState(false); // Derived from balls in zone
    const [isChuteOpen, setIsChuteOpen] = useState(false);
    const [stuckBallId, setStuckBallId] = useState(null);

    // Kickback State
    const [kickbacks, setKickbacks] = useState({ left: true, right: true });

    const ballRefs = useRef(new Map());
    const scoreboardRef = useRef(null);
    const fireworksRef = useRef(null); // Ref for Fireworks
    const processingBallId = useRef(null); // Debounce for bank entry

    // --- Debug Context Value ---


    // ... (Sound Engine) ...
    const useSound = () => {
        const synth = useRef(null);

        useEffect(() => {
            if (!sfxEnabled) return;
            synth.current = new Tone.PolySynth(Tone.Synth).toDestination();
            synth.current.volume.value = -10;

            return () => {
                if (synth.current) {
                    synth.current.dispose();
                    synth.current = null; // Fix: Prevent use after dispose
                }
            };
        }, [sfxEnabled]);

        const lastBumperTime = useRef(0);
        const playBumper = (position) => {
            if (!sfxEnabled || !synth.current) return;
            const now = Date.now();
            if (now - lastBumperTime.current > 50 && Tone.context.state === 'running') {
                synth.current.triggerAttackRelease("C5", "32n");
                lastBumperTime.current = now;
            }
            // Visual Juice: Tier 1 Spark
            if (fireworksRef.current && position) {
                // Spawn slightly above the bumper (y + 1.5)
                fireworksRef.current.spawnSparkBurst([position[0], position[1] + 1.5, position[2]], new THREE.Color('#ec4899'));
            }
        };
        const playFlipper = () => {
            if (!sfxEnabled || !synth.current) return;
            if (Tone.context.state === 'running') synth.current.triggerAttackRelease("G2", "32n", "+0", 0.5);
        };
        const playKickback = () => {
            if (!sfxEnabled || !synth.current) return;
            if (Tone.context.state === 'running') synth.current.triggerAttackRelease("D4", "16n");
            // Visual Juice: Tier 1 Spark (Cyan)
            if (fireworksRef.current) {
                fireworksRef.current.spawnSparkBurst([Math.random() > 0.5 ? 10 : -10, 2, 15], new THREE.Color('#06b6d4'));
            }
        }

        const playLockSound = () => {
            if (!sfxEnabled || !synth.current) return;
            if (Tone.context.state === 'running') {
                // Use shared PolySynth for stability (Low Chord)
                synth.current.triggerAttackRelease(["C3", "E3"], "8n");
            }
        };

        const playFocusLight = () => {
            if (!sfxEnabled || !synth.current) return;
            if (Tone.context.state === 'running') {
                // Subtle high ping
                synth.current.triggerAttackRelease("E5", "32n", "+0", 0.3);
            }
            // Visual Juice: Tier 2 Rocket (Cyan)
            if (fireworksRef.current) {
                fireworksRef.current.spawnRocket([Math.random() * 4 - 2, 2, -20], new THREE.Color('#06b6d4'));
            }
        };

        const playFocusComplete = () => {
            if (!sfxEnabled || !synth.current) return;
            if (Tone.context.state === 'running') {
                // Major Chord
                synth.current.triggerAttackRelease(["C4", "E4", "G4", "C5"], "8n");
            }
            // Visual Juice: Tier 3 Show (Gold)
            if (fireworksRef.current) {
                fireworksRef.current.spawnFocusSequence();
            }
        };

        const playPointsBuildup = () => {
            if (!sfxEnabled || !synth.current) return;
            if (Tone.context.state === 'running') {
                // Subtle high blip, very short
                synth.current.triggerAttackRelease("C6", "64n", "+0", 0.1);
            }
        };

        return { playBumper, playFlipper, playKickback, playLockSound, playFocusLight, playFocusComplete, playPointsBuildup };
    };

    const sound = useSound();

    // Responsive Camera Logic
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Manual Reset Listener (R key)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === 'KeyR') {
                handleScore(0, "RESETTING...");
                ballRefs.current.forEach(ref => {
                    ref.reset();
                    // Ensure it goes to plunger
                    // ActiveBall.reset() uses initial position, which is usually plunger.
                });
                // Also reset kickbacks?
                setKickbacks({ left: true, right: true });
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Initialize Music & Load High Score
    useEffect(() => {
        // Ensure global mute is off (in case other games muted it)
        Tone.Destination.mute = false;

        initMusic();
        setHighScore(getHighScore(GAME_KEYS.PINBALL));
        return () => {
            stopMusic();
            cleanupMusic();
        };
    }, []);

    const handleScore = (points, message) => {
        if (gameState !== 'GAME_OVER') {
            setScore(s => s + points);
            if (message && scoreboardRef.current) {
                scoreboardRef.current.showFlashMessage(message, 1000);
            }

            // Music Phase Logic based on Score
            if (score + points > 100000) {
                setMusicPhase('mid');
            }
        }
    };

    const handleRolloverHit = (index) => {
        if (gameState === 'GAME_OVER') return;
        setFocusLettersLit(prev => {
            if (prev[index]) return prev;
            const newLit = [...prev];
            newLit[index] = true;
            const isComplete = newLit.every(l => l);

            // Side effects in setTimeout to avoid concurrent rendering issues
            setTimeout(() => {
                sound.playFocusLight();
                handleScore(50);

                if (isComplete) {
                    handleScore(50000, "FOCUSED!");
                    sound.playFocusComplete();

                    // Flashing Animation Sequence
                    setTimeout(() => setFocusLettersLit([false, false, false, false, false]), 200);
                    setTimeout(() => setFocusLettersLit([true, true, true, true, true]), 400);
                    setTimeout(() => setFocusLettersLit([false, false, false, false, false]), 600);
                    setTimeout(() => setFocusLettersLit([true, true, true, true, true]), 800);
                    setTimeout(() => setFocusLettersLit([false, false, false, false, false]), 1000); // Final Reset
                }
            }, 0);

            return newLit;
        });
    };

    function spawnBall(pos = [8.5, 1.5, 18]) {
        const id = nextBallId.current++;
        setBalls(prev => [...prev, { id, position: pos, isLocked: false }]);
        // Reset Kickbacks on new ball spawn (per life logic, or per ball logic?)
        // Usually per ball launch.
        setKickbacks({ left: true, right: true });
    }

    function spawnExtraBalls(count) {
        setIsChuteOpen(true);
        // Close chute after animation/spawn
        setTimeout(() => setIsChuteOpen(false), 4000);

        const newBalls = [];
        for (let i = 0; i < count; i++) {
            // Spawn inside the chute: [2, 2.5, -18] (Moved to 12:30 position)
            // Stagger them slightly in Z or time to avoid explosion
            // Let's stagger in position for now
            newBalls.push({ id: nextBallId.current++, position: [2, 2.5, -18 + i * 0.5], isLocked: false });
        }

        // Delay spawn slightly to let door open?
        setTimeout(() => {
            setBalls(prev => [...prev, ...newBalls]);
        }, 500);
    }

    function handleBallEnterBank(id) {
        // Debounce: If we just processed this ball, ignore
        if (processingBallId.current === id) return;

        // Also check if ball is already locked in state (async safety)
        const ball = balls.find(b => b.id === id);
        if (ball && ball.isLocked) return;

        // Set debounce
        processingBallId.current = id;
        // Clear debounce after a short delay (enough for ball to be moved or state to update)
        setTimeout(() => { processingBallId.current = null; }, 500);

        // Find the ball and lock it
        setBalls(prev => prev.map(b => {
            if (b.id === id) return { ...b, isLocked: true, lockPosition: [-6.875, 0.5, -1.25] };
            return b;
        }));

        // Find the ball and lock it
        setBalls(prev => prev.map(b => {
            if (b.id === id) return { ...b, isLocked: true, lockPosition: [-6.875, 0.5, -1.25] };
            return b;
        }));

        // Use ref for logic to avoid side-effects in state updater
        const newCount = lockCountRef.current + 1;
        lockCountRef.current = newCount;

        if (newCount >= 3) {
            // Trigger Multiball
            setLockCount(0);
            lockCountRef.current = 0;

            // Trigger Multiball
            setLockCount(0);
            lockCountRef.current = 0;

            handleScore(10000, "MULTIBALL!");
            setMusicPhase('high'); // High tension music for multiball

            // Visual Juice: Tier 3 Show (Magenta)
            if (fireworksRef.current) {
                fireworksRef.current.spawnShow([0, 5, -15], new THREE.Color('#ec4899'));
            }

            // Unlock the captured ball immediately (or with slight delay)
            setTimeout(() => {
                // Unlock current ball
                setBalls(currentBalls => currentBalls.map(b => {
                    if (b.id === id) return { ...b, isLocked: false, lockPosition: null };
                    return b;
                }));
                // Eject it
                const ref = ballRefs.current.get(id);
                if (ref) ref.eject([-10, 5, 20]); // Shoot to 7 o'clock (Bottom-Left)

                // Spawn extra balls
                spawnExtraBalls(3);
            }, 1000);
        } else {
            // Normal Lock
            setLockCount(newCount);

            // Hold for 3 seconds then release
            handleScore(1000, "BALL LOCKED");
            sound.playLockSound();

            // Points Buildup Loop
            const buildupInterval = setInterval(() => {
                handleScore(50);
                sound.playPointsBuildup();
            }, 100);

            setTimeout(() => {
                clearInterval(buildupInterval); // Stop buildup

                setBalls(currentBalls => currentBalls.map(b => {
                    if (b.id === id) return { ...b, isLocked: false, lockPosition: null };
                    return b;
                }));
                const ref = ballRefs.current.get(id);
                if (ref) ref.eject([-10, 5, 20]); // Shoot to 7 o'clock (Bottom-Left)
            }, 3000);
        }
    }

    function handleKickback(side, id) {
        if (!kickbacks[side]) return;

        // Disable kickback (Consumable)
        setKickbacks(prev => ({ ...prev, [side]: false }));

        // Apply Impulse - Stronger and Steeper (Aim for mid-field)
        const ref = ballRefs.current.get(id);
        if (ref) {
            // Left Side: Aim Right (+X) and Up (-Z). 
            // Right Side: Aim Left (-X) and Up (-Z).
            // Ratio: More vertical force to clear the flipper area.
            const zForce = 80; // Strong upward kick
            const xForceMag = 35; // Moderate inward kick

            const xForce = side === 'left' ? xForceMag : -xForceMag;

            // Add slight Y lift to reduce friction
            ref.eject([xForce, 2, -zForce]);

            handleScore(5000, "KICKBACK!");
            sound.playKickback();
        }
    }

    function handleDrain(id) {
        setBalls(prev => {
            const remaining = prev.filter(b => b.id !== id);

            // Remove ref
            ballRefs.current.delete(id);

            if (remaining.length === 0) {
                if (lives > 1) {
                    setLives(l => l - 1);
                    handleScore(0, "BALL LOST");
                    spawnBall();
                } else {
                    setGameState('GAME_OVER');
                    handleScore(0, "GAME OVER");

                    const isNew = trySetHighScore(GAME_KEYS.PINBALL, score);
                    setIsNewHighScore(isNew);
                    if (isNew) {
                        setHighScore(score);
                    } else {
                        setHighScore(getHighScore(GAME_KEYS.PINBALL));
                    }

                    stopMusic();
                }
            }
            return remaining;
        });
    }

    const handleZoneChange = (inZone, id) => {
        // We only care if ANY ball is in the plunger zone
        // This is a bit tricky with multiple balls.
        // We can check if the ball entering/leaving affects the global state.
        // Simplification: If a ball enters plunger, enable launch.
        // If a ball leaves, we need to check if others are there.
        // For now, let's just use a simple toggle based on the last event, 
        // but ideally we'd track a set of balls in the zone.
        if (inZone) setIsBallInPlunger(true);
        else {
            // Check if any other ball is in plunger? 
            // We don't have easy access to that without tracking per-ball zone state.
            // Let's assume if a ball leaves, and we have multiple balls, we might still have one there.
            // But usually only one is in the plunger.
            // Let's set it to `inZone`. If multiple balls are there, it might flicker if one is out.
            // But practically, only one ball is in the plunger.
            setIsBallInPlunger(inZone);
        }
    };



    const handleStuck = (id) => {
        if (gameState === 'PLAYING') {
            setStuckBallId(id);
            handleScore(0, "BALL STUCK?");
        }
    };

    const handleLaunch = () => {
        if (stuckBallId !== null) {
            // Recover Mode
            const ref = ballRefs.current.get(stuckBallId);
            if (ref) {
                // Workaround: We will remove the ball and spawn a new one in plunger lane.
                setBalls(prev => prev.filter(b => b.id !== stuckBallId));
                spawnBall([8.5, 1.5, 18]); // Spawn new ball in plunger
                setStuckBallId(null);
                handleScore(0, "RECOVERED!");

                // FIX: Ball is now in plunger, so enable button
                setIsBallInPlunger(true);
            }
        } else {
            // Normal Launch
            // Find a ball in the plunger lane
            // We don't track which specific ID is in plunger, but we can iterate refs
            let launched = false;
            ballRefs.current.forEach(ref => {
                if (!launched && ref.launch()) {
                    launched = true;
                    // playSound.playLaunch();
                }
            });

            // FIX: Only disable if we actually launched
            if (launched) setIsBallInPlunger(false);
        }
    };



    const startGame = async () => {
        if (sfxEnabled || musicEnabled) {
            await Tone.start();
        }
        if (musicEnabled) {
            startMusic(); // Start procedural music
        }
        setScore(0);
        setLives(3);
        setGameState('PLAYING');
        setBalls([]);
        nextBallId.current = 0;
        setLockCount(0);
        setFocusLettersLit([false, false, false, false, false]);
        setKickbacks({ left: true, right: true });
        setIsNewHighScore(false); // Reset new high score flag
        // Spawn first ball
        setTimeout(() => spawnBall(), 100);
    };

    const restart = () => {
        setGameState('START');
    };

    // --- Debug Context Value ---
    // eslint-disable-next-line no-unused-vars
    const debugValue = {
        score,
        setScore,
        lives,
        setLives,
        focusLettersLit,
        setFocusLettersLit,
        kickbacks,
        setKickbacks,
        balls,
        setBalls,
        setNextBallId: (val) => { nextBallId.current = val; }, // Expose ref setter
        nextBallId: nextBallId.current,
        spawnBall,
        spawnExtraBalls,
        handleDrain,
        handleKickback,
        // expose any other functions needed by DevPanel
    };

    return (
        <DebugContext.Provider value={debugValue}>
            <div className="w-full h-full bg-slate-950 relative overflow-hidden">
                {/* Zoomed in slightly (38/28) to reduce void visibility while keeping board in frame */}
                <Canvas shadows camera={{ position: [0, isMobile ? 45 : 38, isMobile ? 30 : 28], fov: 50 }}>
                    <color attach="background" args={[COLOR_BG]} />
                    <ambientLight intensity={0.5} />
                    <pointLight position={[0, 20, 0]} intensity={1} color={COLOR_NEON_CYAN} />
                    <directionalLight position={[10, 10, 5]} intensity={1} />
                    <Environment preset="warehouse" />
                    <EffectComposer>
                        <Bloom luminanceThreshold={0.2} luminanceSmoothing={0.9} intensity={1.5} />
                    </EffectComposer>

                    {/* Scoreboard (DMD) */}
                    <Scoreboard ref={scoreboardRef} score={score} />

                    {/* Fireworks System */}
                    <FireworksDisplay ref={fireworksRef} />

                    {/* Launch Button - Attached to Table */}
                    {gameState === 'PLAYING' && (
                        <LaunchButton
                            onClick={handleLaunch}
                            disabled={(!isBallInPlunger && stuckBallId === null) || gameState !== 'PLAYING'}
                            isRecover={stuckBallId !== null}
                        />
                    )}

                    {/* Physics */}
                    <Physics gravity={[0, -50, 30]} iterations={50} step={1 / 120} defaultContactMaterial={{ friction: 0.0, restitution: 0.5 }}>
                        <group rotation={[-0.25, 0, 0]}>
                            <Table
                                onScore={handleScore}
                                onReset={handleDrain} // Pass handleDrain as onReset
                                playSound={sound}
                                ballRefs={ballRefs}
                                onZoneChange={handleZoneChange}
                                focusLettersLit={focusLettersLit}
                                onRolloverHit={handleRolloverHit}
                                balls={balls}
                                onBallEnterBank={handleBallEnterBank}
                                kickbacks={kickbacks}
                                onKickback={handleKickback}
                                isChuteOpen={isChuteOpen}
                                onStuck={handleStuck}
                            />
                        </group>
                    </Physics>
                </Canvas>

                {/* UI Overlay - Lives (Only when Playing) */}
                {gameState === 'PLAYING' && (
                    <div className="absolute top-0 left-0 w-full p-4 flex justify-between items-start pointer-events-none">
                        <div />
                        <div className="flex flex-col items-end bg-slate-900/80 p-3 rounded-lg border border-pink-500/30 backdrop-blur-sm">
                            <h2 className="text-pink-500 font-mono text-[10px] tracking-widest mb-1">LIVES</h2>
                            <div className="flex gap-1">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className={`w-4 h-4 rounded-full border-2 border-pink-500 ${i < lives ? 'bg-pink-500 shadow-[0_0_10px_#ec4899]' : 'bg-transparent'}`} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}



                {/* Start Screen */}
                {gameState === 'START' && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90 backdrop-blur-sm z-50">
                        <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500 mb-4 tracking-tighter drop-shadow-2xl text-center px-4">
                            MINDSTORM PINBALL
                        </h1>

                        <PinballInstructionsGraphic />

                        <div className="flex flex-col items-center justify-center bg-black/80 p-8 rounded-xl border border-cyan-500/50 backdrop-blur-md mt-8">
                            <h1 className="text-4xl font-bold text-cyan-400 mb-2 font-mono tracking-widest glow-text">MINDSTORM PINBALL</h1>
                            <p className="text-cyan-200/70 mb-8 font-mono text-sm">Mindful Arcade Experience</p>

                            <div className="mt-6 flex flex-col items-center gap-2">
                                <div className="text-cyan-400 font-mono text-lg flex items-center gap-2">
                                    <Trophy size={20} />
                                    <span>BEST: {highScore}</span>
                                </div>
                                <p className="text-cyan-200/60 font-mono text-sm animate-pulse text-center px-4">
                                    Left/Right Arrows to Flip • Space to Launch
                                </p>
                            </div>
                            <div className="flex gap-4 mb-8">
                                <label className="flex items-center gap-2 text-cyan-300 cursor-pointer hover:text-cyan-100 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={sfxEnabled}
                                        onChange={(e) => setSfxEnabled(e.target.checked)}
                                        className="w-4 h-4 accent-cyan-500 bg-gray-900 border-cyan-500 rounded"
                                    />
                                    <span className="font-mono text-sm">SFX</span>
                                </label>
                                <label className="flex items-center gap-2 text-cyan-300 cursor-pointer hover:text-cyan-100 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={musicEnabled}
                                        onChange={(e) => setMusicEnabled(e.target.checked)}
                                        className="w-4 h-4 accent-cyan-500 bg-gray-900 border-cyan-500 rounded"
                                    />
                                    <span className="font-mono text-sm">MUSIC</span>
                                </label>
                            </div>

                            <button
                                onClick={startGame}
                                className="group relative px-8 py-3 bg-cyan-900/50 hover:bg-cyan-800/50 text-cyan-100 font-mono text-xl rounded-lg border border-cyan-500/50 transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] flex items-center gap-3"
                            >
                                <Play className="w-6 h-6 fill-current" />
                                START GAME
                            </button>
                        </div>

                        <div className="mt-8 text-center opacity-60">
                            <p className="text-slate-500 font-mono text-[10px] tracking-widest uppercase mb-2 border-b border-slate-800 pb-1 inline-block">Desktop Controls</p>
                            <p className="text-slate-400 font-mono text-xs flex items-center justify-center gap-2">
                                <span className="text-cyan-400 text-lg">←</span> / <span className="text-pink-400 text-lg">→</span> ARROWS to Flip
                            </p>
                            <p className="text-slate-400 font-mono text-xs">
                                <span className="text-green-400">ENTER</span> to Launch Ball
                            </p>
                        </div>
                    </div>
                )}

                {/* Game Over Screen */}
                {gameState === 'GAME_OVER' && (
                    <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center z-50 backdrop-blur-md">
                        <h1 className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-500 mb-6 tracking-tighter filter drop-shadow-[0_0_10px_rgba(0,255,255,0.5)]">
                            GAME OVER
                        </h1>
                        <div className="bg-slate-900/50 p-8 rounded-2xl border border-white/10 backdrop-blur-xl flex flex-col items-center gap-4">
                            <p className="text-slate-400 font-mono text-sm tracking-widest">FINAL SCORE</p>
                            <p className="text-white text-6xl font-mono mb-4 neon-text">{score.toString().padStart(7, '0')}</p>
                            <button onClick={restart} className="px-10 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-xl rounded-full flex items-center gap-3 transition-all transform hover:scale-105 hover:shadow-[0_0_30px_#00ffff]">
                                <RotateCcw size={24} /> RESTART GAME
                            </button>
                        </div>
                    </div>
                )}
                {/* Dev Panel */}
                <DevPanel />
            </div>
        </DebugContext.Provider>
    );
};

export default MindstormPinball;
